//https://threejs.org/docs/index.html?q=interleaved#api/en/core/InterleavedBuffer

/*
observer objects -> unobserve
*/


import * as THREE from 'three';
import { getHeight } from './terrainConfig.js';
import { MeshBVH } from 'three-mesh-bvh';
import CustomShaderMaterial from 'three-custom-shader-material/vanilla';
import Stats from 'three/examples/jsm/libs/stats.module';

import Utils from './Utils.js'
import { TerrainManager } from './TerrainManager.js';
import powerlinesShader from './shaders/powerlines.js';
import { createVideoShaderMaterial } from './materials/videoShaderMaterial.js';
import stonefigureShader from './shaders/stonefigure.js';
import videoFadeShader from './shaders/videoFade.js';
import { createFadeShaderMaterial } from './materials/fadeShaderMaterial.js';
import ProjectedMaterial from './patchProjectorMaterial.js';

import WorldScene from './WorldScene.js';
import { UIManager } from './UIManager.js';
import { PlayerController } from './PlayerController.js';
import ModelLoader from './ModelLoader.js';
import SoundManager from './SoundManager.js';



/********************************************************************
// Vanilla Javascript
********************************************************************/

/********************************************************************
// Check if Mobile device
********************************************************************/
let visitedFromMobileDevice;

function isMobileDevice() {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent);
}


if (isMobileDevice()) {
    visitedFromMobileDevice = true;
} else {
    visitedFromMobileDevice = false;
}

const grassContainer = document.getElementById('container-contact');

/********************************************************************
// Intersection Observer(s) for Canvas
********************************************************************/

// Intersection Observer setup for opening scene
const observerOptions = {
    root: null, // Use the viewport as the root
    rootMargin: '0px',
    threshold: 0.3 // Trigger when 30% of the target is visible
};

//intersection observer for detecting opening scene out of view
const observerCallback = (entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            // Start or resume animation
            fnStartRendering();

        } else {
            // Pause animation
            fnStopRendering();
        }
    });
};

const observer = new IntersectionObserver(observerCallback, observerOptions);
const targetElement = document.getElementById('container-opening-scene');
observer.observe(targetElement);

// Intersection Observer setup for contact scene

//intersection observer for detecting opening scene out of view
const observerContactSceneCallback = (entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            if (grassScene) {
                grassScene.startRendering();
            } else if (!grassSceneInitializing) {
                grassSceneInitializing = true;
                import('./GrassScene.js').then(({ default: GrassScene }) => {
                    grassScene = new GrassScene(grassContainer, visitedFromMobileDevice, modelLoader);
                    if (storedSkyboxPromise) {
                        grassScene.setEnvMapFromPromise(storedSkyboxPromise);
                    }
                });
            }
        } else {
            if (grassScene) {
                grassScene.stopRendering();
            }
        }
    });
};

const observerContactScene = new IntersectionObserver(observerContactSceneCallback, observerOptions);
observerContactScene.observe(grassContainer);



/********************************************************************
// Handle Button Navigation
********************************************************************/

//sound button
const soundToggleBtn = document.getElementById('btn-sound-toggle');

const uiManager = new UIManager({ visitedFromMobileDevice });
const sectionScene = uiManager.sectionScene;

/********************************************************************
// THREEJS STUFF
********************************************************************/

/********************************************************************
// Scene Constants
********************************************************************/

const PERSON_HEIGHT = 22.0; //21
const DISTANCE_TEXTURE_SWAP = 300.0;
//const DISTANCE_TEXTURE_DISPOSE = 650.0;

//LOD
const DISTANCE_LOD0 = 100;
const DISTANCE_LOD1 = 200;
const DISTANCE_LOD2 = 300;

//Environment Map intensities



/********************************************************************
// Scene Setup
********************************************************************/

const canvasContainer = document.querySelector('#container-opening-scene');
const worldScene = new WorldScene(canvasContainer, visitedFromMobileDevice);

let grassScene = null;
let grassSceneInitializing = false;
let storedSkyboxPromise = null;

const renderer = worldScene.getRenderer();
const camera = worldScene.getCamera();



//const axesHelper = new THREE.AxesHelper(1000);
//worldScene.scene.add(axesHelper);

//show stats, updated in animation loop

// const stats = Stats();
// stats.showPanel(0);
// document.body.appendChild(stats.dom);



/********************************************************************
// Sounds
********************************************************************/
const soundManager = new SoundManager(camera);
soundManager.setMasterVolume(1.00);
soundManager.setAmbienceVolume(0.75);
soundManager.setEffectsVolume(0.90);



let soundOn, soundsAreloaded = false; //windSoundIsLoaded removed - unused

soundToggleBtn.addEventListener('click', () => {
    // Toggle sound state
    soundOn = !soundOn;

    // Update the icon based on state
    if (soundOn) {
        soundToggleBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="1em" height="1em" fill="currentColor"><path d="M533.6 32.5C598.5 85.2 640 165.8 640 256s-41.5 170.7-106.4 223.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C557.5 398.2 592 331.2 592 256s-34.5-142.2-88.7-186.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zM473.1 107c43.2 35.2 70.9 88.9 70.9 149s-27.7 113.8-70.9 149c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C475.3 341.3 496 301.1 496 256s-20.7-85.3-53.2-111.8c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zm-60.5 74.5C434.1 199.1 448 225.9 448 256s-13.9 56.9-35.4 74.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C393.1 284.4 400 271 400 256s-6.9-28.4-17.7-37.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zM301.1 34.8C312.6 40 320 51.4 320 64l0 384c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L131.8 352 64 352c-35.3 0-64-28.7-64-64l0-64c0-35.3 28.7-64 64-64l67.8 0L266.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3z"/></svg>';
        soundToggleBtn.classList.add('active');
        // Code to turn sound on
        //soundManager.resumeAll();
        if (!soundsAreloaded) {
            soundManager.resumeAll();
            fnLoadSoundFiles();

        } else {
            soundManager.resumeAll();
        }

    } else {
        soundToggleBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="1em" height="1em" fill="currentColor"><path d="M301.1 34.8C312.6 40 320 51.4 320 64l0 384c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L131.8 352 64 352c-35.3 0-64-28.7-64-64l0-64c0-35.3 28.7-64 64-64l67.8 0L266.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3zM425 167l55 55 55-55c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-55 55 55 55c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-55-55-55 55c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l55-55-55-55c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z"/></svg>';
        soundToggleBtn.classList.remove('active');
        // Code to turn sound off
        soundManager.pauseAll();
    }
});



const soundConfigs = {
    'projector': { path: './assets/sounds/projector2.mp3', refDistance: 60, audibleRange: 170 },
};
const soundSources = new Map();

function fnCheckIfSoundSourcesShouldPlay(playerPosition) {
    // Check each sound source
    //if (soundSources) {
    soundSources.forEach((data, id) => {
        //console.log(data)

        const distance = playerPosition.distanceTo(data.audioSource.position);
        const type = data.type;

        // If within audible range but not playing
        if (distance < soundConfigs[type].audibleRange && !data.isPlaying) {
            soundManager.play(type);
            data.isPlaying = true;

            soundSources.set(id, data);
        }
        // If outside audible range but still playing
        else if (distance > soundConfigs[type].audibleRange && data.isPlaying) {
            soundManager.stop(type);
            data.isPlaying = false;

            soundSources.set(id, data);
        }
    });
}

function fnLoadSoundFiles() {

    let loadingcount = 0;
    let totalSounds = soundSources.size + 2; // wind and ambient

    function checkAllSoundsAreLoaded() {
        loadingcount++;
        if (loadingcount >= totalSounds) {
            soundsAreloaded = true;
        }
    }

    //load positional sound files
    soundSources.forEach((data, id) => {
        soundManager.loadPositionalSound(data.name, data.path, data.audioSource, soundConfigs[data.type].refDistance, () => {
            soundManager.play(data.name);
            soundManager.stop(data.name);
            checkAllSoundsAreLoaded();
        });
    });

    // Load ambient sound and play it when loaded
    soundManager.loadAmbientSound('outdoors', './assets/sounds/ambience.mp3', true, () => {
        soundManager.playAmbience('outdoors', true, 3.0);

        checkAllSoundsAreLoaded();
    });


    const windSound = new THREE.Audio(soundManager.listener);
    soundManager.audioLoader.load('./assets/sounds/wind.mp3', (buffer) => {
        windSound.setBuffer(buffer);
        windSound.setLoop(true);
        windSound.setVolume(0.3 * soundManager.masterVolume);
        windSound.play();

        soundManager.sounds['wind'] = windSound;

        checkAllSoundsAreLoaded();
    });
}



/********************************************************************
 // Check for Tab visibility
********************************************************************/
let gameIsActive = true;


function fnStartRendering() {
    renderer.setAnimationLoop(animate);
    gameIsActive = true;
    soundToggleBtn.classList.remove('hide');

    if (soundOn) {
        soundManager.resumeAll();
        soundToggleBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="1em" height="1em" fill="currentColor"><path d="M533.6 32.5C598.5 85.2 640 165.8 640 256s-41.5 170.7-106.4 223.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C557.5 398.2 592 331.2 592 256s-34.5-142.2-88.7-186.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zM473.1 107c43.2 35.2 70.9 88.9 70.9 149s-27.7 113.8-70.9 149c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C475.3 341.3 496 301.1 496 256s-20.7-85.3-53.2-111.8c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zm-60.5 74.5C434.1 199.1 448 225.9 448 256s-13.9 56.9-35.4 74.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C393.1 284.4 400 271 400 256s-6.9-28.4-17.7-37.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zM301.1 34.8C312.6 40 320 51.4 320 64l0 384c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L131.8 352 64 352c-35.3 0-64-28.7-64-64l0-64c0-35.3 28.7-64 64-64l67.8 0L266.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3z"/></svg>';
        soundToggleBtn.classList.add('active');
    }
}

function fnStopRendering() {
    renderer.setAnimationLoop(null);
    gameIsActive = false;
    soundManager.pauseAll();

    //soundOn = false;
    soundToggleBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="1em" height="1em" fill="currentColor"><path d="M301.1 34.8C312.6 40 320 51.4 320 64l0 384c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L131.8 352 64 352c-35.3 0-64-28.7-64-64l0-64c0-35.3 28.7-64 64-64l67.8 0L266.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3zM425 167l55 55 55-55c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-55 55 55 55c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-55-55-55 55c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l55-55-55-55c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z"/></svg>';
    soundToggleBtn.classList.remove('active');
    soundToggleBtn.classList.add('hide');
}

// Detect when the user switches to a different tab or window
document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        fnStopRendering();  // Pause when the tab is inactive

    } else if (window.scrollY === 0 || window.pageYOffset === 0) {
        fnStartRendering(); // Resume when the tab is active again
    }
});





/********************************************************************
// Texture Loading
********************************************************************/

const textureLoader = new THREE.TextureLoader();



/********************************************************************
// Skybox loading
********************************************************************/

// Skybox loading (handled by WorldScene)
worldScene.loadSkybox();
storedSkyboxPromise = worldScene.skyboxPromise;
if (grassScene) {
    grassScene.setEnvMapFromPromise(storedSkyboxPromise);
}

/********************************************************************
// Landscape : Simplex Noise
// Now imported from terrainConfig.js (shared with grassWorker.js)
// simplex, getHeight, smoothstep, flatAreas
********************************************************************/

/********************************************************************
// Factory Entry Check (post processing swap)
********************************************************************/

let activeComposer = worldScene.getComposerDefault();

const factoryEntranceTrigger = new THREE.Box3(
    new THREE.Vector3(-190, -50, -350),  // box around factory
    new THREE.Vector3(200, 50, 90)
);
factoryEntranceTrigger.translate(new THREE.Vector3(1200, getHeight(1200, 1200), 1200));


let previousPlayerInsideFactory = false;
let factoryCheckCooldown = 0;
const FACTORY_CHECK_INTERVAL = 100;

function fnCheckIfPlayerIsInFactoryTrigger(playerPosition, deltaTime) {
    factoryCheckCooldown -= deltaTime * 1000;

    if (factoryCheckCooldown <= 0) {
        factoryCheckCooldown = FACTORY_CHECK_INTERVAL;

        const isPlayerInTrigger = factoryEntranceTrigger.containsPoint(playerPosition);

        if (isPlayerInTrigger !== previousPlayerInsideFactory) {
            previousPlayerInsideFactory = isPlayerInTrigger;

            if (isPlayerInTrigger) {
                activeComposer = worldScene.getComposerFactoryInterior();

                //windowMaterial.transparent = true;

                const windSound = soundManager.sounds['wind'];
                if (windSound) {
                    windSound.setVolume(0.1 * soundManager.masterVolume);
                }
            } else {
                activeComposer = worldScene.getComposerDefault();

                const windSound = soundManager.sounds['wind'];
                if (windSound) {
                    windSound.setVolume(0.3 * soundManager.masterVolume);
                }
            }
        }
    }
}

/********************************************************************
// Video Texture for Rock Projection
********************************************************************/
const videoProject = document.createElement('video');
videoProject.src = './assets/nature/video.webm';
videoProject.loop = true;
videoProject.muted = true;
videoProject.play();

const videoTextureProject = new THREE.VideoTexture(videoProject);
videoTextureProject.minFilter = THREE.LinearFilter;
videoTextureProject.magFilter = THREE.LinearFilter;
videoTextureProject.format = THREE.RGBAFormat;
videoTextureProject.generateMipmaps = false;



function fnCreateVideoMaterial() {
    return new CustomShaderMaterial({
        baseMaterial: THREE.MeshStandardMaterial,
        uniforms: {
            uVideoTexture: { value: null },
            uDiffuseMap: { value: null },
            uTerrainHeight: { value: 0.0 },
            uFadeHeight: { value: 5.3 }, // Height where fade effect occurs
            uProjectorPosition: { value: new THREE.Vector3() },
            uProjectorDirection: { value: new THREE.Vector3() },
            uProjectorMatrix: { value: new THREE.Matrix4() },
            uProjectorFOV: { value: 45.0 * (Math.PI / 180.0) }, // FOV in radians
            uProjectorAspect: { value: 16 / 9 }, // Aspect ratio of your video
            //uBaseColor: { value: new THREE.Color(0.1, 0.1, 0.1) },
            uProjectionIntensity: { value: 0.8 },
            uVignette: { value: 0.2 }, // Vignette effect for old-school look
            uBlendFactor: { value: 0.25 }, // Control how much the projection blends with diffuse (0-1)
            uMinZDistance: { value: null }
        },
        vertexShader: videoFadeShader.vert,
        fragmentShader: videoFadeShader.frag,
        side: THREE.FrontSide,
        fog: true
    })
}








/********************************************************************
// Materials
********************************************************************/

/** Powerlines Material */
const powerlinesShaderMaterial = new THREE.ShaderMaterial({
    vertexShader: powerlinesShader.vert,
    fragmentShader: powerlinesShader.frag,
    uniforms: {
        uTime: { value: 0.0 },
        colorStart: { value: new THREE.Color(0x4D474A) },
        colorEnd: { value: new THREE.Color(0xF2F8FF) }
    },
    transparent: true, // Enable transparency
});


const fadeShaderMaterial = createFadeShaderMaterial({
    fadeHeight: 5.5,
    brightness: 1.0,
    side: THREE.FrontSide
});

const factoryShader = new CustomShaderMaterial({
    baseMaterial: THREE.MeshStandardMaterial,
    uniforms: {
        uDiffuseMap: { value: null },
        uHasDiffuseMap: { value: false },
        uColor: { value: new THREE.Color(1, 0, 0) },
        uTerrainHeight: { value: null },
        uFadeHeight: { value: 5.5 },
        uMeshPosition: { value: null },
        uBrightness: { value: 1.0 }
    },
    vertexShader: fadeShaderMaterial.__csm.vertexShader,
    fragmentShader: fadeShaderMaterial.__csm.fragmentShader,
    side: THREE.DoubleSide,
    fog: true
});




/********************************************************************
// Load Models
********************************************************************/
let allModelsLoaded = false;
let allModelsAddedToScene = false;

const loadingManager = new THREE.LoadingManager();
const loadingElement = document.querySelector('#text-loading');

loadingManager.onStart = function (url, itemsLoaded, itemsTotal) {
    //console.log(`Started loading: ${url} (${itemsLoaded}/${itemsTotal})`);
};

loadingManager.onProgress = function (url, itemsLoaded, itemsTotal) {
    //console.log('Loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.');
    const progress = Math.floor((itemsLoaded / itemsTotal) * 100);
    //console.log(`Loading file: ${url}. Loaded ${itemsLoaded}/${itemsTotal} files.`);
    //console.log(`Loading: ${progress}%`)
    loadingElement.textContent = `${progress}%`;

};

loadingManager.onLoad = function () {
    //console.log("All models loaded!");
    allModelsLoaded = true;
};


const modelLoader = new ModelLoader(loadingManager, renderer);
const modelRegistry = new Map();




async function fnLoadPowerlinesModel(url) {
    const position = { x: 385, z: -300 }; //360, 275 (10, 12, 10)
    const name = 'powerlines';
    let mesh;

    const model = await modelLoader.loadModel(url);

    model.scale.set(11, 13, 11);
    model.rotateY(-Math.PI / 4);
    model.position.set(position.x, getHeight(position.x, position.z) - 2, position.z);

    mesh = model.children.find(child => child.isMesh);

    // Create powerlines material from fadeShaderMaterial clone
    const powerlinesMaterial = fadeShaderMaterial.clone();
    powerlinesMaterial.uniforms.uColor.value = new THREE.Vector3(0.175, 0.175, 0.175);
    powerlinesMaterial.uniforms.uDiffuseMap.value = null;
    powerlinesMaterial.uniforms.uMeshPosition.value = new THREE.Vector3(position.x, getHeight(position.x, position.z) - 2, position.z);
    powerlinesMaterial.uniforms.uTerrainHeight.value = getHeight(position.x, position.z);
    powerlinesMaterial.uniforms.uFadeHeight.value = 5.0;
    powerlinesMaterial.uniforms.uHasDiffuseMap.value = false;
    powerlinesMaterial.envMap = worldScene.envMap;
    powerlinesMaterial.envMapIntensity = 0.4;
    powerlinesMaterial.fog = false;
    powerlinesMaterial.metalness = 1.0;
    powerlinesMaterial.roughness = 0.6;

    model.children[1].material = powerlinesShaderMaterial;
    model.children[3].material = powerlinesShaderMaterial;
    model.children[4].material = powerlinesShaderMaterial;
    model.children[6].material = powerlinesShaderMaterial;

    model.children[0].children[0].material = powerlinesMaterial;
    model.children[0].children[1].material = powerlinesMaterial;
    model.children[0].children[2].material = powerlinesMaterial;

    model.traverse((child) => {
        if (child.isMesh && child.name.includes('Power_Lines')) {
            child.material.transparent = true;
            child.material.opacity = 0.45;
        }
    })

    worldScene.scene.add(model);
    Utils.fnAddModelToRegistry(modelRegistry, name, mesh, new THREE.Vector3(position.x, getHeight(position.x, position.z) - 2, position.z), null, true, false, null, false, null, null);
};

const windowMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.85,
    metalness: 0.5,
    opacity: 0.96, //0.75
    transparent: true,
    side: THREE.DoubleSide,
    fog: true
});


// Prewarming function
async function prewarmWindowMaterial(renderer, camera) {
    const prewarmGeometry = new THREE.BoxGeometry(1, 1, 1);
    const prewarmBox = new THREE.Mesh(prewarmGeometry, windowMaterial);

    worldScene.scene.add(prewarmBox);

    // Async compile shaders without blocking
    await renderer.compileAsync(worldScene.scene, camera);

    worldScene.scene.remove(prewarmBox);
    prewarmGeometry.dispose();
}
if (!visitedFromMobileDevice) {
    prewarmWindowMaterial(renderer, camera);
}

async function fnLoadFactoryModel(url) {

    const position = new THREE.Vector3(1200, getHeight(1200, 1200), 1200)
    const name = 'factory_new';
    let mesh, material, diffuseMap, model, colliderMesh, colliderBVH, collisionRadius;


    model = await modelLoader.loadModel(url);
    //console.log(model)

    mesh = model.children[0].children[1];


    model.rotateY((-Math.PI / 2));
    model.position.set(position);
    model.scale.set(10, 10, 10);




    diffuseMap = mesh.material.map;
    colliderMesh = model.children[2];
    //console.log(colliderMesh)
    material = factoryShader;

    mesh.material = material;
    material.uniforms.uHasDiffuseMap.value = true;
    material.uniforms.uDiffuseMap.value = diffuseMap;
    material.uniforms.uFadeHeight.value = 30.0;
    material.uniforms.uTerrainHeight.value = getHeight(1200, 1200)
    material.uniforms.uMeshPosition.value = new THREE.Vector3(1200, getHeight(1200, 1200), 1200)

    // const LOD1Material = new THREE.MeshStandardMaterial({
    //     color: 0xFF0000,
    //     map: diffuseMap,
    //     fog: true,
    // });

    material.envMap = worldScene.envMap;
    material.envMapIntensity = 1.5;

    windowMaterial.map = diffuseMap;
    // Apply material to window meshes
    const windowMeshLOD0 = model.children[0].children[0];
    const windowMeshLOD1 = model.children[1].children[0];


    windowMeshLOD0.material = windowMaterial;
    windowMeshLOD1.material = windowMaterial;

    // Set environment map properties
    windowMeshLOD0.material.envMap = worldScene.envMap;
    windowMeshLOD1.material.envMap = worldScene.envMap;
    windowMeshLOD0.material.envMapIntensity = 1.5;
    windowMeshLOD1.material.envMapIntensity = 1.5;

    //windowMeshLOD0.visible = false;

    /*
        model.children[0].children[1].material = windowMaterial;
        model.children[2].children[1].material = windowMaterial;
        model.children[0].children[1].material.envMap = worldScene.envMap;
        model.children[2].children[1].material.envMap = worldScene.envMap;
        model.children[0].children[1].material.envMapIntensity = 0.5; //1.2
        model.children[2].children[1].material.envMapIntensity = 0.5; //1.2
    */
    //model.children[0].children[1].visible = false;
    //model.children[2].children[1].visible = false;

    //material.needsUpdate = true;
    //windowMaterial.needsUpdate = true;


    const LOD0 = model.children[0];
    const LOD1 = model.children[1];
    //console.log(LOD1.children[1].material)
    //LOD1.children[1].material = LOD1Material;

    //LOD 1 mesh material
    LOD1.children[1].material.envMap = worldScene.envMap;
    LOD1.children[1].material.envMapIntensity = 1.2;

    const lod = new THREE.LOD();
    lod.addLevel(LOD0, 900);
    lod.addLevel(LOD1, 1100);



    lod.rotateY((-Math.PI / 2));
    lod.position.set(position.x, position.y, position.z);
    lod.scale.set(10.0, 10.0, 10.0);

    //colliderMesh = model.children.find(child => child.isMesh && child.name === 'collider');
    colliderMesh.visible = false;


    colliderMesh.rotateY((-Math.PI / 2));
    colliderMesh.position.set(position.x, position.y, position.z);
    colliderMesh.scale.set(10, 10, 10);

    // Ensure world matrix is updated before applying it to geometry
    colliderMesh.updateMatrixWorld(true);

    const geom = colliderMesh.geometry.clone();
    geom.applyMatrix4(colliderMesh.matrixWorld);

    // Create BVH from the transformed geometry
    geom.boundsTree = new MeshBVH(geom);

    // Assign the transformed geometry to the mesh
    colliderMesh.geometry = geom;

    // Add to the scene
    colliderBVH = colliderMesh.geometry.boundsTree;
    collisionRadius = colliderBVH.geometry.boundingSphere.radius + 170;

    Utils.fnAddModelToRegistry(modelRegistry, name, mesh, position, diffuseMap, true, true, 700, false, colliderBVH, collisionRadius);
    worldScene.scene.add(lod)

    // console.log('Scaled collider geometry bounds - fac:', colliderMesh.geometry.boundingBox);
    // console.log('Scaled collider bounding sphere - fac:', colliderMesh.geometry.boundingSphere);

}


async function fnLoadFactoryInteriorModel(url) {
    //const position = { x: 1200, z: 1200 };
    const position = new THREE.Vector3(1200, getHeight(1200, 1200), 1200)
    const name = 'factory_interior';
    let mesh, material, diffuseMap, model, colliderMesh, colliderBVH, collisionRadius;


    model = await modelLoader.loadModel(url);


    mesh = model.children[0];
    const LOD1 = model.children[0].clone();

    diffuseMap = mesh.material.map;

    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // Transform children locally (no position - that goes on LOD)
    mesh.rotateY((-Math.PI / 2));
    mesh.scale.set(9.98, 9.98, 9.98);

    LOD1.rotateY((-Math.PI / 2));
    LOD1.scale.set(9.98, 9.98, 9.98);

    // Basic red material for LOD1 testing
    LOD1.material = new THREE.MeshStandardMaterial({
        map: mesh.material.map,  // Get map directly from original
        envMap: worldScene.envMap,
        envMapIntensity: 1.5
    });

    //create LOD - position goes HERE, not on children
    const lod = new THREE.LOD();
    lod.position.set(position.x, position.y, position.z);
    lod.addLevel(mesh, 0);         // projection material when close (0-400 units)
    lod.addLevel(LOD1, 900);       // red basic material when far (400+ units)



    //colliderMesh = model.children[1];
    material = fnCreateVideoMaterial();

    mesh.material = material;

    // Update shader uniforms
    const videoTest = document.createElement('video');
    videoTest.src = './assets/glass/super8.webm';
    videoTest.loop = true;
    videoTest.muted = true; // Important for autoplay
    videoTest.crossOrigin = 'anonymous';
    videoTest.play();

    // Create a video texture
    const videoTextureTest = new THREE.VideoTexture(videoTest);
    videoTextureTest.minFilter = THREE.LinearFilter;
    videoTextureTest.magFilter = THREE.LinearFilter;
    videoTextureTest.format = THREE.RGBAFormat;

    // Create a larger target surface
    const surfaceWidth = 75;  // Larger width
    //const surfaceHeight = surfaceWidth * (9 / 16);// Larger height
    const surfaceHeight = surfaceWidth * (3 / 4);// Larger height

    // Define the projection area relative to the surface size
    // These values control the relative size and position of the projection on the surface
    const projectionParams = {
        // Size of projection as a fraction of the surface dimensions
        relativeWidth: 0.5,    // 50% of surface width
        relativeHeight: 0.5,   // 60% of surface height
        // Center position of projection (0,0 is center, values from -0.5 to 0.5)
        offsetX: 0.0,         // Offset to the left
        offsetY: -0.25           // Offset slightly upward
    };

    const targetCenter = new THREE.Vector3(
        projectionParams.offsetX * surfaceWidth,
        projectionParams.offsetY * surfaceHeight,
        0
    );

    // Create the projector object (this will be the source of projection)
    const projector = new THREE.Object3D();

    projector.position.set(
        projectionParams.offsetX * surfaceWidth - 650,  // Center X over target
        getHeight(1200, 1200) + 20,                     // Position high above
        710   // Center Z over target
    );

    //projector.rotation.set(0, -Math.PI / 2, 0);  // -90 degrees on X axis

    projector.lookAt(
        projectionParams.offsetX * surfaceWidth + 50,
        projectionParams.offsetY * surfaceHeight,
        0
    ); // Point at the center of the desired projection area

    worldScene.scene.add(projector);

    //projector.position.set(0, 0, 0);




    //soundManager.play('projector');

    const projectorAudioSource = new THREE.Object3D();
    projectorAudioSource.position.set(1250, getHeight(1250, 1200) + PERSON_HEIGHT, 1200);
    worldScene.scene.add(projectorAudioSource);
    //soundManager.loadPositionalSound('projector', './assets/sounds/projector2.mp3', projectorAudioSource, 60.0);


    soundSources.set(projectorAudioSource.id, { audioSource: projectorAudioSource, type: 'projector', name: 'projector', path: './assets/sounds/projector2.mp3', isPlaying: false });


    // Get the static projector position and direction
    const projectorPosition = new THREE.Vector3();
    projector.getWorldPosition(projectorPosition);

    const projectorDirection = new THREE.Vector3();
    projector.getWorldDirection(projectorDirection);
    projectorDirection.negate(); // Flip to point forward

    // Create a static view matrix for the projector
    const projectorViewMatrix = new THREE.Matrix4();
    projectorViewMatrix.lookAt(
        projectorPosition,
        new THREE.Vector3().addVectors(projectorPosition, projectorDirection),
        new THREE.Vector3(0, 1, 0)
    );

    // Calculate field of view to cover only a portion of the surface
    // We'll use a narrower FOV to restrict the projection area
    const distance = projectorPosition.distanceTo(targetCenter);
    const targetWidth = surfaceWidth * projectionParams.relativeWidth;
    const targetHeight = surfaceHeight * projectionParams.relativeHeight;

    // Calculate FOV based on the desired projection width and distance
    const fovX = 2 * Math.atan(targetWidth / (2 * distance));
    const fovY = 2 * Math.atan(targetHeight / (2 * distance));
    const fov = Math.max(fovX, fovY); // Use the larger angle to ensure coverage
    const aspect = targetWidth / targetHeight;

    const projectorProjectionMatrix = new THREE.Matrix4();
    projectorProjectionMatrix.makePerspective(-aspect * Math.tan(fov / 2), aspect * Math.tan(fov / 2), Math.tan(fov / 2), -Math.tan(fov / 2), 1, 1000);

    // Combine into one static projector matrix
    const projectorMatrix = new THREE.Matrix4().multiplyMatrices(
        projectorProjectionMatrix,
        projectorViewMatrix
    );

    material.uniforms.uProjectorPosition.value = projectorPosition;
    material.uniforms.uProjectorDirection.value = projectorDirection;
    material.uniforms.uProjectorMatrix.value = projectorMatrix;
    material.uniforms.uDiffuseMap.value = diffuseMap;
    material.uniforms.uVideoTexture.value = videoTextureTest;
    material.uniforms.uMinZDistance.value = (position.z + 500); //500
    material.uniforms.uTerrainHeight.value = getHeight(1200, 1200);
    material.uniforms.uFadeHeight.value = 2.5;

    material.envMap = worldScene.envMap;
    material.envMapIntensity = 2.10;




    Utils.fnAddModelToRegistry(modelRegistry, name, mesh, position, diffuseMap, true, true, 700, false, colliderBVH, collisionRadius);
    worldScene.scene.add(lod)

}

/********************************************************************
// VIDEO PROJECTION TEST
********************************************************************/


async function fnLoadStoneModelWithProjection({
    modelURL = null,
    name = null,
    position = new THREE.Vector2(0, 0),
    scale = 1,
    envMapIntensity = 0.4,
    isFaded = false,
    fadeHeight = 15.0,
} = {}) {

    let colliderBVH;

    const height = getHeight(position.x, position.y);
    // Example: model loaded with MeshStandardMaterial
    const model = await modelLoader.loadModel(modelURL);


    const collider = model.children.find(child => child.isMesh && child.name === 'collider');
    const lod1 = model.children.find(child => child.isMesh && child.name === 'lod1');
    const lod2 = model.children.find(child => child.isMesh && child.name === 'lod2');
    collider.visible = false;
    //console.log(collider)

    collider.rotateY((Math.PI * 2) * 3 / 4);
    collider.position.set(position.x, getHeight(position.x, position.y), position.y);
    collider.scale.set(scale, scale, scale);

    //const mesh = model.children.find(child => child.isMesh && child.name === name);
    const diffuseMap = lod1.material.map;
    model.scale.set(scale);

    const lod = new THREE.LOD();
    lod.addLevel(lod1, DISTANCE_LOD0);
    lod.addLevel(lod2, DISTANCE_LOD1);


    lod.position.set(position.x, getHeight(position.x, position.y), position.y);
    lod.scale.set(scale, scale, scale);
    lod.rotateY((Math.PI * 2) * 3 / 4);

    // Ensure world matrix is updated before applying it to geometry
    collider.updateMatrixWorld(true);

    const geom = collider.geometry.clone();
    geom.applyMatrix4(collider.matrixWorld);

    // Create BVH from the transformed geometry
    geom.boundsTree = new MeshBVH(geom);

    // Assign the transformed geometry to the mesh
    collider.geometry = geom;

    // Add to the scene
    colliderBVH = collider.geometry.boundsTree;
    const collisionRadius = colliderBVH.geometry.boundingSphere.radius + 10;


    const projector = new THREE.PerspectiveCamera(30, 16 / 9, 0.01, 2);
    projector.position.set(position.x, height + scale, position.y + 60);
    //const helper = new THREE.CameraHelper(projector);
    //worldScene.scene.add(helper);


    const videoProjectionMaterial = new ProjectedMaterial({
        camera: projector, // the camera that acts as a projector
        texture: videoTextureProject, // the texture being projected
        textureScale: 0.9, // scale down the texture a bit
        textureOffset: new THREE.Vector2(0.0, -0.1), // you can translate the texture if you want
        cover: true, // enable background-size: cover behaviour, by default it's like background-size: contain
        map: diffuseMap, // the color of the object if it's not projected on
        roughness: 0.9, // you can pass any other option that belongs to MeshPhysicalMaterial
        side: THREE.FrontSide,
        envMap: worldScene.envMap,
        envMapIntensity: envMapIntensity,
        fog: true,
        opacity: 1.0,
        isFaded: isFaded,
        fadeHeight: fadeHeight,
        terrainHeight: height,
    });

    lod1.material = videoProjectionMaterial;
    lod2.material = videoProjectionMaterial;
    // and when you're ready project the texture on the box!
    videoProjectionMaterial.project(lod1);
    videoProjectionMaterial.project(lod2);

    worldScene.scene.add(lod);

    Utils.fnAddModelToRegistry(modelRegistry, name, lod1, new THREE.Vector3(position.x, 0, position.y), diffuseMap, true, true, DISTANCE_TEXTURE_SWAP, false, colliderBVH, collisionRadius);
}


async function fnLoadHQTexture(data) {

    if (!data) return;

    const material = data.mesh.material;
    const name = data.name;

    if (!data.hqTexture) {
        const textureDiffuse = await modelLoader.loadKTX2Texture(`./assets/models/${name}/base_color_cmp.ktx2`);

        if (material.uniforms && material.uniforms.uDiffuseMap !== undefined) {
            material.uniforms.uDiffuseMap.value.dispose();
            material.uniforms.uDiffuseMap.value = textureDiffuse;
        } else {
            material.map = textureDiffuse;
        }


        data.hqTexture = textureDiffuse;
    } else {
        material.uniforms.uDiffuseMap.value = data.hqTexture;
    }
    //data.mesh.material.needsUpdate = true;
}


// // Function to unload HQ texture (remove from GPU memory)
// function fnUnloadHQTexture(data) {
//     if (!data.hqTexture) return;
//     const material = data.mesh.material;
//     if (material.uniforms.uDiffuseMap.value) {
//         material.uniforms.uDiffuseMap.value.dispose();
//         material.uniforms.uDiffuseMap.value = data.lqTexture;
//     }
//     material.needsUpdate = true;
// }

/*
async function fnLoadRockModels() {
    try {
        // Load the factory model
        const model = await modelLoader.loadModel('./assets/models/rock_model.glb');


        model.scale.set(45, 45, 45)
        model.position.set(100, getHeight(100, 800) - 15, 800)

        model.children[0].material.color = new THREE.Color(0.8, 0.8, 0.8)
        model.children[0].material.envMap = worldScene.envMap;
        model.children[0].material.envMapIntensity = 0.35;
        model.children[0].material.roughness = 1.0;
        model.children[0].material.side = THREE.FrontSide;

        worldScene.scene.add(model)


        model.children.forEach((child) => {
            const clonedMesh = child.clone();


            clonedMesh.position.set(0, getHeight(0, 0) - 2, 0)
            clonedMesh.scale.set(2.45, 2.45, 2.45)

            clonedMesh.material.envMap = worldScene.envMap;
            clonedMesh.material.envMapIntensity = 0.35;

            
                        if (child.name === 'LOD0') {
                            lodRocks.addLevel(clonedMesh, 150);
                            //clonedMesh.scale.set(1, 1, 1)
                        } else if (child.name === 'LOD1') {
                            lodRocks.addLevel(clonedMesh, 200);
                            //clonedMesh.scale.set(1, 1, 1)
                        } else if (child.name === 'LOD2') {
                            lodRocks.addLevel(clonedMesh, 250);
                            //clonedMesh.scale.set(1, 1, 1)
                        }
                    
            //clonedMesh.updateMatrixWorld(true);
            //clonedMesh.applyMatrix4(child.matrixWorld);
            //clonedMesh.scale.set(1.7, 1.7, 1.7)
            //clonedMesh.position.set(100, getHeight(100, 1000), 1000)

            //lodRocks.updateMatrixWorld(true);

            //clonedMesh.updateMatrixWorld(true);
            //clonedMesh.matrixAutoUpdate = false;
            //clonedMesh.material.side = THREE.FrontSide;
            //clonedMesh.material.transparent = true;


        });



        lodRocks.position.set(100, getHeight(100, 1000), 1000)
        worldScene.scene.add(lodRocks);

        //model.scale.set(100, 100, 100);

        //console.log(model.children[0])



    } catch (error) {
        //console.error('Error loading models:', error);
    }
}
*/

function createStoneFigureBaseMaterial() {
    return new CustomShaderMaterial({
        baseMaterial: THREE.MeshStandardMaterial,
        uniforms: {
            uDiffuseMap: { value: null },
            hologramColor: { value: new THREE.Color(0xab52e3) },
            time: { value: 0 },
            uOpacity: { value: 0.75 },
            scanLineFrequency: { value: 0.1 },
            scanLineSpeed: { value: 0.5 },
            distortionAmount: { value: 0.0 },
            glowAmount: { value: 2.0 },
            flickerSpeed: { value: 0.05 },
            flickerIntensity: { value: 0.05 },
            textureInfluence: { value: 0.85 },
            effectsIntensity: { value: 0.0 },
            positionScale: { value: 1.0 },
            uBrightness: { value: 1.0 },
            uTerrainHeight: { value: null },
            uFadeHeight: { value: 1.5 },
        },
        vertexShader: stonefigureShader.vert,
        fragmentShader: stonefigureShader.frag,
        side: THREE.FrontSide,
        transparent: true,
        fog: false,

    });
}


function createStoneFigureMaterial() {
    const baseMaterial = createStoneFigureBaseMaterial();
    const clone = baseMaterial.clone();
    //clone.uniforms = THREE.UniformsUtils.clone(clone.uniforms);
    return clone;
}



const stoneFigureParams = [
    { url: './assets/models/horse_figure/horse_figure.glb', name: 'horse_figure', position: Utils.fnGetRandomPosition(600, 800, 800), scale: new THREE.Vector3(2.5, 2.5, 2.5), rotation: -Math.PI / 4, material: createStoneFigureMaterial(), isAnimating: false, color: new THREE.Color(0xf6e3ff) },
    //{ url: './assets/models/dog_figure/dog_figure.glb', name: 'dog_figure', position: Utils.fnGetRandomPosition(600, 800, 850), scale: new THREE.Vector3(3.5, 3.5, 3.5), rotation: -Math.PI / 4, material: createStoneFigureMaterial(), isAnimating: false, color: new THREE.Color(0xf6e3ff) },
    { url: './assets/models/mouse_figure/mouse_figure.glb', name: 'mouse_figure', position: Utils.fnGetRandomPosition(800, 1000, 1000), scale: new THREE.Vector3(2.5, 2.5, 2.5), rotation: -Math.PI / 4, material: createStoneFigureMaterial(), isAnimating: false, color: new THREE.Color(0xf6e3ff) },
    { url: './assets/models/snake_figure/snake_figure.glb', name: 'snake_figure', position: Utils.fnGetRandomPosition(650, 500, 500), scale: new THREE.Vector3(2.5, 2.5, 2.5), rotation: -Math.PI / 4, material: createStoneFigureMaterial(), isAnimating: false, color: new THREE.Color(0xf6e3ff) },
    { url: './assets/models/goat_figure/goat_figure.glb', name: 'goat_figure', position: Utils.fnGetRandomPosition(600, 500, 500), scale: new THREE.Vector3(2.5, 2.5, 2.5), rotation: Math.PI - Math.PI / 4, material: createStoneFigureMaterial(), isAnimating: false, color: new THREE.Color(0xf6e3ff) },
];


async function fnLoadStoneFigureModel(url, name, position, scale, rotation, material, color) {

    let model, mesh, diffuseMap, colliderBVH;


    model = await modelLoader.loadModel(url);
    diffuseMap = model.children[0].material.map;

    mesh = model.children.find(child => child.isMesh && child.name === 'LOD0');


    mesh.material = material;
    material.transparent = true;
    material.uniforms.uDiffuseMap.value = diffuseMap;
    material.uniforms.uTerrainHeight.value = getHeight(position.x, position.z);
    material.uniforms.uFadeHeight.value = 8.0;
    material.uniforms.uBrightness.value = 0.75;
    material.uniforms.hologramColor.value = color;
    material.envMap = worldScene.envMap;
    material.envMapIntensity = 0.45;

    const LOD1 = model.children.find(child => child.isMesh && child.name === 'LOD1');
    const LOD2 = model.children.find(child => child.isMesh && child.name === 'LOD2');
    const collider = model.children.find(child => child.isMesh && child.name === 'Collider');
    //collider.rotateY(rotation);

    LOD1.material = material;
    LOD2.material = material;


    const lod = new THREE.LOD();
    lod.addLevel(mesh, DISTANCE_LOD0);
    lod.addLevel(LOD1, DISTANCE_LOD1);
    lod.addLevel(LOD2, DISTANCE_LOD2);

    lod.rotateY(rotation);
    lod.position.set(position.x, getHeight(position.x, position.z), position.z);
    lod.scale.set(scale.x, scale.y, scale.z);



    //collider.visible = true;
    collider.rotateY(rotation);
    collider.position.set(position.x, getHeight(position.x, position.z), position.z);
    collider.scale.set(scale.x, scale.y, scale.z);

    // Ensure world matrix is updated before applying it to geometry
    collider.updateMatrixWorld(true);

    const geom = collider.geometry.clone();
    geom.applyMatrix4(collider.matrixWorld);

    // Create BVH from the transformed geometry
    geom.boundsTree = new MeshBVH(geom);

    // Assign the transformed geometry to the mesh
    collider.geometry = geom;

    // Add to the scene
    colliderBVH = collider.geometry.boundsTree;
    const collisionRadius = colliderBVH.geometry.boundingSphere.radius;


    worldScene.scene.add(lod)
    //renderer.compile(lod, camera, worldScene.scene);
    Utils.fnAddModelToRegistry(modelRegistry, name, mesh, new THREE.Vector3(position.x, getHeight(position.x, position.z), position.z), diffuseMap, true, true, DISTANCE_TEXTURE_SWAP, false, colliderBVH, collisionRadius);

}


//Load models for desktop
if (!visitedFromMobileDevice) {
    window.addEventListener('load', () => {
        stoneFigureParams.forEach((params) => {
            fnLoadStoneFigureModel(params.url, params.name, params.position, params.scale, params.rotation, params.material, params.color);
        });

        fnLoadFactoryModel('./assets/models/factory_new/factory.glb');
        fnLoadFactoryInteriorModel('./assets/models/factory_interior/factory_interior.glb');

        fnLoadStoneModelWithProjection({
            modelURL: './assets/models/video_rock/video_rock.glb',
            name: 'video_rock',
            position: new THREE.Vector2(-400, 200),
            scale: 10,
            envMapIntensity: 0.25,
            isFaded: true,
            fadeHeight: 18.5
        });


    });

    //fnLoadRockVideoProjectionModel('./assets/models/video_rock/videoRockPreload.glb', 12);

}

// Models that load on all platforms (including mobile)
fnLoadPowerlinesModel('./assets/models/powerlines/powerlines.glb');



function fnToggleFigureAnimation(stoneFigureParams, figureName) {
    for (let i = 0; i < stoneFigureParams.length; i++) {
        if (stoneFigureParams[i].name === figureName) {
            //stoneFigureParams[i].material.uniforms.effectsIntensity.value = 1.0;
            setTimeout(() => {
                stoneFigureParams[i].isAnimating = true;
            }, 2000);

            break; // Exit loop once found
        }
    }
}







/********************************************************************
// Video Plane
********************************************************************/
// Create a video element
const video = document.createElement('video');
video.src = './assets/introvideo640.mp4';  // Set the path to your video file


video.setAttribute('webkit-playsinline', 'webkit-playsinline'); // For older Safari versions
video.setAttribute('playsinline', 'playsinline'); // For modern browsers
video.muted = true;
video.autoPlay = true;
video.loop = true;
video.play();


// Create a texture from the video element
const videoTexture = new THREE.VideoTexture(video);
videoTexture.minFilter = THREE.LinearFilter;
videoTexture.magFilter = THREE.LinearFilter;
videoTexture.format = THREE.RGBAFormat;
videoTexture.generateMipmaps = false;
videoTexture.colorSpace = THREE.SRGBColorSpace;



const videoShaderMaterial = createVideoShaderMaterial(videoTexture, {
    edgeTransparency: 0.15,
    globalOpacity: 0.85
});



// Then handle both paused and low-power conditions
//if (video.paused || video.autoplay && !video.playing) {

if (video.paused) {
    const fallbackTexture = textureLoader.load('./assets/videoFallback.webp', () => {
        videoShaderMaterial.uniforms.videoTexture.value = fallbackTexture;
    });

}


document.body.addEventListener("touchstart", function () {

    //set texture to video
    videoShaderMaterial.uniforms.videoTexture.value = videoTexture;
    video.play();

}, { once: true });

// Create a plane geometry
const videoGeometry = new THREE.PlaneGeometry(384, 216, 32, 16);  // 10x10 segments for smooth edges

// Create a mesh with the geometry and custom shader material
const videoPlane = new THREE.Mesh(videoGeometry, videoShaderMaterial);
videoPlane.position.set(0, getHeight(0, 0) + 110.0, 0);

// Video plane is added when grass workers are done with initial load


/********************************************************************
// Position Camera at pageload
********************************************************************/

let startPosition, endPosition;

if (visitedFromMobileDevice) {
    camera.rotation.x = 0;
    camera.far = 1100;
    camera.fov = 60;

    startPosition = new THREE.Vector3(0, getHeight(0, 550) + PERSON_HEIGHT, 550);
    endPosition = new THREE.Vector3(0, getHeight(0, 450) + PERSON_HEIGHT, 450);

    if (window.matchMedia("(orientation: landscape)").matches) {
        startPosition = new THREE.Vector3(0, getHeight(0, 350) + PERSON_HEIGHT, 350);
        endPosition = new THREE.Vector3(0, getHeight(0, 250) + PERSON_HEIGHT, 250);
        camera.far = 950;
    }

} else {
    startPosition = new THREE.Vector3(0, getHeight(0, 420) + PERSON_HEIGHT, 420); // Starting position
    endPosition = new THREE.Vector3(0, getHeight(0, 320) + PERSON_HEIGHT, 320);  // Ending position
    camera.rotation.x = Math.PI / 16;
}

camera.position.set(startPosition.x, startPosition.y, startPosition.z);


/********************************************************************
// Animate Camera
********************************************************************/
// Animation state
const cameraAnimationState = {
    startTime: null,
    isAnimating: false, // Flag to control animation
    duration: 5000,
    fnCheckOrientationCanRun: false
};

// Easing function (Ease In-Out Quad)
function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

function fnAnimateCamera() {
    // Time management
    const currentTime = performance.now();
    if (cameraAnimationState.isAnimating) {
        if (!cameraAnimationState.startTime) {
            cameraAnimationState.startTime = currentTime;
        }

        const elapsed = currentTime - cameraAnimationState.startTime;
        let progress = Math.min(elapsed / cameraAnimationState.duration, 1); // Clamp progress between 0 and 1
        // Apply easing function to progress
        progress = easeInOutQuad(progress);

        // Interpolate position
        const interpolatedPosition = new THREE.Vector3().lerpVectors(startPosition, endPosition, progress);

        // Calculate dynamic y position
        const dynamicY = getHeight(interpolatedPosition.x, interpolatedPosition.z) + PERSON_HEIGHT;


        // Set camera position
        camera.position.set(interpolatedPosition.x, dynamicY, interpolatedPosition.z);


        // Stop animation when completed
        if (progress >= 1) {
            cameraAnimationState.isAnimating = false;
            cameraAnimationState.fnCheckOrientationCanRun = true;
            if (!visitedFromMobileDevice) {
                document.querySelector('.wasd-icon').classList.remove('hide');
            }

        }
    }
}

function fnCheckOrientation() {
    if (cameraAnimationState.fnCheckOrientationCanRun) {

        if (window.matchMedia("(orientation: landscape)").matches && visitedFromMobileDevice) {
            camera.far = 750;
            camera.rotation.x = 0;
            camera.position.set(0, getHeight(0, 250) + PERSON_HEIGHT, 250);


        } else if (window.matchMedia("(orientation: portrait)").matches && visitedFromMobileDevice) {
            camera.far = 1100;
            camera.rotation.x = 0;
            camera.position.set(0, getHeight(0, 450) + PERSON_HEIGHT, 450);

        }
    }
};




/********************************************************************
// PlayerController
********************************************************************/

const clock = new THREE.Clock();

let playerController = null;
if (!visitedFromMobileDevice) {
    playerController = new PlayerController({
        camera,
        renderer,
        sectionScene,
        modelRegistry,
        stoneFigureParams,
        fnToggleFigureAnimation,
        personHeight: PERSON_HEIGHT,
        speed: 350.0,
        endPosition,
        gameIsActive: () => gameIsActive,
        hideWasdIcon: () => uiManager.hideWasdIcon()
    });
    uiManager.setControls(playerController.controls);
} else {
    // Mobile touch input for camera rotation
    let startX = 0;
    let startY = 0;
    let isTouching = false;

    sectionScene.addEventListener('touchstart', (event) => {
        isTouching = true;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
    });

    sectionScene.addEventListener('touchmove', (e) => {
        if (!isTouching) return;
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;

        const deltaX = currentX - startX;
        const deltaY = currentY - startX;

        const rotationSpeed = 0.005;
        camera.rotation.y -= deltaX * rotationSpeed;

        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
    });

    sectionScene.addEventListener('touchend', () => {
        isTouching = false;
    });
}





/********************************************************************
// Terrain Manager
********************************************************************/
const terrainManager = new TerrainManager({ worldScene, camera });
terrainManager.loadInitialTerrain(1);

/********************************************************************
// Function to run start animation and welcome screen fade, at page load
********************************************************************/

function fnCreateOnceFunction() {
    let hasBeenCalled = false;
    return function () {
        if (!hasBeenCalled) {

            fnFadeOutWelcomeScreen();
            cameraAnimationState.isAnimating = true;

            hasBeenCalled = true;
        }
    };
}
const fnOnTerrainComplete = fnCreateOnceFunction()


/********************************************************************
// Function to fade out welcome screen and add Video Plane to scene
********************************************************************/

function fnFadeOutWelcomeScreen() {
    const welcomeScreen = document.getElementById("welcome-screen");

    welcomeScreen.classList.add("hidden");

    worldScene.scene.add(videoPlane);

    setTimeout(() => {
        welcomeScreen.style.display = "none";
    }, 2100); // Matches the duration of the CSS transition

}


/********************************************************************
// Animate Function
********************************************************************/


let textureswapCheckCooldown = 0;
const TEXTURE_SWAP_CHECK_INTERVAL = 100;

let renderInfoCooldown = 0;
const RENDER_INFO_INTERVAL = 2000;


/********************************************************************
// Animate function
********************************************************************/

function animate() {
    const deltaTime = clock.getDelta();
    const playerPosition = camera.position;


    terrainManager.update(playerPosition, deltaTime);

    textureswapCheckCooldown -= deltaTime * 1000;

    if (visitedFromMobileDevice) {
        fnCheckOrientation();
    } else {
        if (!cameraAnimationState.isAnimating) {
            playerController.update(deltaTime);
        }
    }

    if (textureswapCheckCooldown <= 0) {
        textureswapCheckCooldown = TEXTURE_SWAP_CHECK_INTERVAL;
        modelRegistry.forEach((data, id) => {

            //if (!Utils.fnIsInFrustum(data.mesh, camera)) return;

            //distance from mesh to player
            const distance = camera.position.distanceToSquared(data.position)
            const distanceTextureSwap = data.distanceTextureSwap * data.distanceTextureSwap;

            if (data.needsTextureSwapToHQ && distance < distanceTextureSwap) {
                if (!data.textureIsLoaded) {
                    data.textureIsLoaded = true;
                    data.needsTextureSwapToHQ = false;
                    //update model registry
                    modelRegistry.set(id, data);
                    //load texture
                    fnLoadHQTexture(data);
                }
            }
            /*
            if (!data.needsTextureSwapToHQ && distance > DISTANCE_TEXTURE_DISPOSE) {
    
                data.needsTextureSwapToHQ = true;
                data.textureIsLoaded = false;
                modelRegistry.set(id, data);
                fnUnloadHQTexture(data);
            }
            */
        });
    }


    if ((visitedFromMobileDevice || allModelsLoaded) && !allModelsAddedToScene && terrainManager.allGrassComputed) {

        const allAdded = [...modelRegistry.values()].every(model => model.isAddedToScene);


        if (allAdded) {
            //renderer.compile(worldScene.scene, camera);
            fnOnTerrainComplete();
            allModelsAddedToScene = true;
        }
    }




    fnAnimateCamera();




    //shader uniform updates
    powerlinesShaderMaterial.uniforms.uTime.value += deltaTime * 3.0;
    videoShaderMaterial.uniforms.uTime.value += deltaTime * 0.25;

    stoneFigureParams.forEach((item) => {
        if (item.isAnimating && item.material) {
            if (item.material.uniforms.effectsIntensity.value < 1.0) {
                item.material.uniforms.effectsIntensity.value += deltaTime / 5;
                //console.log(item.material.uniforms.effectsIntensity.value)
            }
            item.material.uniforms.time.value += deltaTime * 2.0;
        }
    });



    //function to change post-processing composer for indoor env
    fnCheckIfPlayerIsInFactoryTrigger(playerPosition, deltaTime)


    fnCheckIfSoundSourcesShouldPlay(playerPosition)


    // Render once at the end of your animate function
    activeComposer.render(deltaTime);

    //stats.update();

    // renderInfoCooldown -= deltaTime * 1000;
    // if (renderInfoCooldown <= 0) {
    //     renderInfoCooldown = RENDER_INFO_INTERVAL;
    //     const mem = renderer.info.memory;
    //     const render = renderer.info.render;
    //     console.log(`[Renderer] geometries: ${mem.geometries} | textures: ${mem.textures} | calls: ${render.calls} | triangles: ${render.triangles} | programs: ${renderer.info.programs?.length ?? '?'}`);
    // }

    //videoTexture.needsUpdate = true;

}
